import { useState, useEffect, useCallback } from "react";
import {
  CheckCircle,
  User as UserIcon,
  Phone,
  Mail,
  Save,
  Key,
  ShoppingCart,
  Wallet,
  RefreshCw,
  CreditCard,
  Trash2
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { API_URL } from "../config";
import { authFetch } from "../utils/authFetch";
import { ReferralLinkButton } from "./ReferralLinkButton";


export function UserProfile({ onSellerUpdate }) {
  // Профиль
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    phone: "",
    refCode: "",
    isSeller: false,
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);

  // Сброс пароля
  const [isResetting, setIsResetting] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState(null);

  // Состояния загрузки
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- РЕФЕРАЛЬНЫЙ БАЛАНС / ВЫПЛАТА ---
  const [bonusLoading, setBonusLoading] = useState(true);         // грузим баланс
  const [bonusRub, setBonusRub] = useState(0);                     // рубли с сервера
  const [bonusKop, setBonusKop] = useState(0);                     // копейки
  const [payoutLoading, setPayoutLoading] = useState(false);       // идёт вывод сейчас
  const [payoutMessage, setPayoutMessage] = useState(null);        // {type:'success'|'error', text:string}

  const [cards, setCards] = useState([]);
  const [isSeller, setIsSeller] = useState(false);
  const [isAutoRenew, setIsAutoRenew] = useState(false);
  const [hasCard, setHasCard] = useState(false);
  const [dateSubExp, setDateSubExp] = useState(false);
  const [isActiveSub, setIsActiveSub] = useState(false);
  const [binding, setBinding] = useState(false);
  const [requestKey, setRequestKey] = useState();
  const [addCardPaymentId, setAddCardPaymentId] = useState();
  
  
  const refreshSeller = useCallback(async () => {
    try {
      const r = await authFetch(`${API_URL}/about-user`, { method: "GET" });
      if (!r.ok) return;
      const me = await r.json();
      setIsAutoRenew(!!me?.subscription_auto_renew);
      setHasCard(!!me?.has_card);
      setDateSubExp(me?.subscription_expires_at);
      setIsActiveSub(!!me?.is_active_subscription);
      setIsSeller(me.has_card === true && me.is_active_subscription === true);
    } catch {}
  }, []);

  const fetchPayoutCards = async () => {
    const token = localStorage.getItem('token');
    const res = await authFetch(`${API_URL}/seller/card-payouts-info/details`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.cards) ? data.cards : [];
  };

  const loadCards = async () => {
    try {
      const list = await fetchPayoutCards();
      setCards(list);
      return list;
    } catch (e) {
      console.error('Ошибка при получении карт:', e);
      setCards([]);
      return [];
    }
  };

  const deleteCard = async (cardId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await authFetch(`${API_URL}/seller/payout-card/${cardId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        return;
      }
      
      setHasCard(false);
      onSellerUpdate(false);
      await loadCards();
      await refreshSeller();
    } catch (err) {
      console.error('Ошибка при удалении карты:', err);
    }
  };

  // 1) Загрузка данных профиля
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await authFetch(`${API_URL}/about-user`, {
          headers: {
            "Authorization": `Bearer ${token}`
          }
        });
        if (!res.ok) throw new Error(`Ошибка ${res.status}`);
        const data = await res.json();
        setProfileData({
          name: data.name,
          email: data.email,
          phone: data.phone || "",
          isSeller: data.has_card === true && data.is_active_subscription === true,
          refCode: data.ref_code,
        });
      } catch (err) {
        console.error(err);
        setError("Не удалось загрузить профиль");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
    loadCards();
    refreshSeller();
  }, []);


  // 3) Сохранение профиля
  const handleProfileSave = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await authFetch(`${API_URL}/profile`, {
        method: "PATCH",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(profileData)
      });
      if (!res.ok) throw new Error(`Ошибка ${res.status}`);
      setProfileSaved(true);
      setIsEditingProfile(false);
      setTimeout(() => setProfileSaved(false), 3000);
    } catch (err) {
      console.error(err);
      setError("Не удалось сохранить изменения");
    }
  };

  // 4) Сброс пароля
  const handleResetEmail = async () => {
    setIsResetting(true);
    setResetError(null);
    try {
      const res = await authFetch(`${API_URL}/auth/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ email: profileData.email })
      });
      if (!res.ok) throw new Error(`Ошибка ${res.status}`);
      setResetSent(true);
    } catch (err) {
      console.error(err);
      setResetError("Не удалось отправить письмо для сброса");
    } finally {
      setIsResetting(false);
    }
  };

  const unsubscribe = async () => {
    const jwt = localStorage.getItem("token");
    if (!jwt) {
      alert("Пожалуйста, войдите");
      return;
    }

    try {
      setLoading(true);
      const res = await authFetch(`${API_URL}/seller/unsubscription`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      });

      if (res.status === 401) {
        alert("Сессия истекла. Войдите заново.");
        return;
      }
      if (!res.ok) {
        const detail = await res.text().catch(() => "");
        alert(`Не удалось отменить подписку. ${detail || ""}`);
        return;
      }
      refreshSeller();

    } catch (e) {
      console.error(e);
      alert("Произошла ошибка при отмене подписки.");
    } finally {
      setLoading(false);
    }
  }

  async function handleBindingCard() {
    setBinding(true);
    try {
      const jwt = localStorage.getItem('token');
      if (!jwt) return alert('Пожалуйста, войдите');

      // Запустить привязку карты
      const res = await authFetch(`${API_URL}/seller/bind-card-payouts`, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json"
        }
      });
      if (!res.ok) {
        return alert('Не удалось начать привязку карты');
      }

      const { paymentUrl, customerKey, addCardPaymentId, requestKey } = await res.json();

      if (!paymentUrl) return alert('Не получили paymentUrl из ответа сервера');

      localStorage.setItem(
        'payoutBindMeta',
        JSON.stringify({ addCardPaymentId, requestKey })
      );
      localStorage.setItem('payoutBindInProgress', '1'); // флаг для поллинга после возврата
      window.location.href = paymentUrl;

      // const resUpd = await authFetch(`${API_URL}/seller/update-card-db`, {
      //   method: 'POST',
      //   body: JSON.stringify({ customerKey, addCardPaymentId, requestKey })
      // });

      // if (resUpd.success === false) {
      //   return alert('Не удалось обновить данные карт на сервере');
      // }

      return

    } catch (err) {
    alert(err.message);
    console.error(err);
    } finally {
    setBinding(false);
    }
  }

  useEffect(() => {
    const raw = localStorage.getItem('payoutBindMeta');
    if (raw) {
      const { addCardPaymentId, requestKey } = JSON.parse(raw);
      setAddCardPaymentId(addCardPaymentId);
      setRequestKey(requestKey);
    }
  }, []);


  const pollForPayoutCard = async ({
    timeoutMs = 22_000,
    intervalMs = 2_000
  } = {}) => {
    const startedAt = Date.now();

    const raw = localStorage.getItem('payoutBindMeta');
    if (raw) {
      const { addCardPaymentId, requestKey } = JSON.parse(raw);
      setAddCardPaymentId(addCardPaymentId);
      setRequestKey(requestKey);
    }

    while (Date.now() - startedAt < timeoutMs) {
      const list = await fetchPayoutCards();

      console.log(
        '[poll] cards:',
        list.map(c => ({ id: c.CardId, status: c.Status }))
      );

      const active = list.find(
        c => c.Status === 'A' && c.CardId
      );

      if (active) {
        await authFetch(`${API_URL}/seller/payout-card/use`, {
          method: 'POST',
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ cardId: active.CardId, addCardPaymentId, requestKey })
        });

        await loadCards();
        await refreshSeller();
        setHasCard(true);
        onSellerUpdate(true);

        localStorage.removeItem('payoutBindInProgress');
        localStorage.removeItem('payoutBindMeta');

        return { success: true, card: active };
      }

      await new Promise(r => setTimeout(r, intervalMs));
    }

    // Таймаут
    console.warn('[poll] timeout: card not activated');

    localStorage.removeItem('payoutBindInProgress');
    return { success: false };
  };


  // Первичная загрузка и "фоновый" поллинг, если мы уходим на привязку
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await loadCards();
      const needPolling = localStorage.getItem('payoutBindInProgress') === '1';

      console.log('[poll-effect] mounted');
      console.log('[poll-effect] needPolling:', needPolling);

      if (!needPolling || cancelled) return;
      const ok = await pollForPayoutCard({ timeoutMs: 22_000, intervalMs: 2_000 });
      console.log('[poll-effect] result:', ok);
      if (!ok.success && !cancelled) {
        localStorage.removeItem('payoutBindInProgress');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  

  if (loading) {
    return <p className="p-6 text-center">Загрузка профиля…</p>;
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      <div className="text-center space-y-2">
        <h1 className="gradient-text text-2xl sm:text-3xl font-bold">Профиль</h1>
        <p className="text-gray-600 text-sm sm:text-base">
          Управляйте личной информацией и выплатами
        </p>
      </div>

      {/* Успешно сохранили профиль */}
      {profileSaved && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <span className="text-green-700 text-sm sm:text-base">
              Профиль успешно обновлен
            </span>
          </div>
        </div>
      )}

      {error && (
        <div className="text-red-600 text-center">{error}</div>
      )}

      {/* --- Личная информация --- */}
      <div className="bg-white border rounded-lg">
        <div className="p-4 sm:p-6 border-b">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <UserIcon /> Личная информация
            </h2>
            {!isEditingProfile && (
              <Button
                variant="outline"
                className="text-[#2563eb] border-[#2563eb] hover:bg-blue-50 text-sm w-full sm:w-auto"
                onClick={() => setIsEditingProfile(true)}
              >
                Редактировать
              </Button>
            )}
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <Label htmlFor="name" className="text-sm sm:text-base">
              Имя и фамилия
            </Label>
            <Input
              id="name"
              value={profileData.name}
              onChange={e =>
                setProfileData({ ...profileData, name: e.target.value })
              }
              disabled={!isEditingProfile}
              className="pl-10 text-sm sm:text-base"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="text-sm sm:text-base">
              Телефон
            </Label>
            <div className="relative">
              <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="phone"
                value={profileData.phone}
                onChange={e =>
                  setProfileData({ ...profileData, phone: e.target.value })
                }
                disabled={!isEditingProfile}
                className="pl-10 text-sm sm:text-base"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm sm:text-base">
              Email
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="email"
                value={profileData.email}
                onChange={e =>
                  setProfileData({ ...profileData, email: e.target.value })
                }
                disabled={!isEditingProfile}
                className="pl-10 text-sm sm:text-base"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="is-seller" className="text-sm sm:text-base">
              Ваш статус
            </Label>
            <div className="relative">
              <ShoppingCart className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="is-seller"
                value={
                  profileData.isSeller
                    ? "Можете продавать документы"
                    : "Не можете продавать документы"
                }
                disabled={true}
                className="pl-10 text-sm sm:text-base"
              />
            </div>
          </div>

          {isActiveSub && (
            <p className="text-gray-600 text-sm sm:text-base">
              Ваша подписка активна до {new Date(dateSubExp).toLocaleDateString()}.
            </p>
          )}

          {isActiveSub && hasCard && (
            <ReferralLinkButton />
          )}
          
          {isActiveSub && isAutoRenew && (
              <Button
                type="button"
                onClick={unsubscribe}
                className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white mt-4"
              >
                Прервать подписку
              </Button>
          )}
        
          {isEditingProfile && (
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button
                onClick={handleProfileSave}
                className="bg-[#2563eb] hover:bg-blue-700 text-white w-full sm:w-auto"
              >
                <Save className="h-4 w-4 mr-2" />
                Сохранить
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  // откатиться к данным с сервера
                  setIsEditingProfile(false);
                  setError(null);
                  setProfileSaved(false);
                  setLoading(true);
                  authFetch(`${API_URL}/about-user`, {
                    headers: {
                      "Authorization": `Bearer ${localStorage.getItem("token")}`
                    }
                  })
                    .then(r => r.json())
                    .then(d => {
                      setProfileData({
                        name: d.name,
                        email: d.email,
                        phone: d.phone || "",
                        isSeller: d.has_card === true && d.is_active_subscription === true ,
                        refCode: d.ref_code,
                      });
                    })
                    .catch(() => setError("Ошибка при откате"))
                    .finally(() => setLoading(false));
                }}
                className="w-full sm:w-auto"
              >
                Отмена
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border rounded-lg">
          <div className="bg-white rounded-lg border">
              <div className="p-4 sm:p-6 border-b">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <h2 className="font-semibold flex items-center gap-2 text-sm sm:text-base">
                    <CreditCard className="h-4 w-4 sm:h-5 sm:w-5" />
                    Ваши карты
                  </h2>
                </div>
              </div>
              <div className="p-4 sm:p-6">
                {isActiveSub && !hasCard && (
                <div className="space-y-4">
                  <p>Привяжите карту для выплат, чтобы загружать документы.</p>
                  <Button
                      onClick={handleBindingCard}
                      disabled={binding}
                      className="bg-gradient-to-r from-[#3466F8] via-[#CF4EE6] to-[#F6275D] text-white"
                  >
                      {binding ? 'Привязываем…' : 'Привязать карту'}
                  </Button>
                </div>
                )}
                {isActiveSub && hasCard && (
                  <div className="space-y-4">
                    {cards.map((card) => (
                      <div key={card.CardId} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg gap-3">
                        <div className="flex items-center gap-4">
                          <CreditCard className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-sm sm:text-base">{card.Pan}</p>
                            <p className="text-xs text-gray-500">Действует до {card.ExpDate}</p>
                            <button onClick={() => deleteCard(card.CardId)}>
                            <Trash2 />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
          </div>
      </div>

      {/* --- Сброс пароля --- */}
      <div className="bg-white border rounded-lg">
        <div className="p-4 sm:p-6 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Key /> Забыли пароль?
          </h2>
        </div>
        <div className="p-6">
          <p className="text-sm sm:text-base mb-4">
            Чтобы сменить пароль, отправим ссылку для сброса на ваш Email.
          </p>
          <Button
            onClick={handleResetEmail}
            disabled={isResetting}
            className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white"
          >
            {isResetting ? "Отправка ссылки..." : "Сбросить пароль"}
          </Button>
        </div>
      </div>
    </div>
  );
}
